(() => {
    const releaseVideo = (video) => {
        if (!video) return;

        video.pause();
        video.removeAttribute('src');
        video.querySelectorAll('source').forEach((source) => source.removeAttribute('src'));
        video.load();
        video.remove();
    };

    const videoModal = {
        element: null,
        content: null,
        closeButton: null,
        activeVideo: null,
        activeOwner: null,
        activeTrigger: null,

        ensure() {
            if (this.element) return;

            const element = document.createElement('div');
            element.className = 'vl-video-modal';
            element.setAttribute('role', 'dialog');
            element.setAttribute('aria-modal', 'true');
            element.setAttribute('aria-label', 'Video player');
            element.setAttribute('aria-hidden', 'true');
            element.innerHTML = `
                <div class="vl-video-modal__overlay" data-vl-video-modal-close></div>
                <div class="vl-video-modal__body">
                    <button class="vl-video-modal__close" type="button" aria-label="Close video" data-vl-video-modal-close>
                        <svg viewBox="0 0 40 40" aria-hidden="true" focusable="false">
                            <circle cx="20" cy="20" r="18" fill="currentColor"/>
                            <path d="M14 14L26 26M26 14L14 26" fill="none" stroke="white" stroke-width="2" stroke-linecap="round"/>
                        </svg>
                    </button>
                    <div class="vl-video-modal__content" data-vl-video-modal-content></div>
                </div>
            `;

            element.querySelectorAll('[data-vl-video-modal-close]').forEach((button) => {
                button.addEventListener('click', () => this.close());
            });

            document.addEventListener('keydown', (event) => {
                if (event.key === 'Escape' && this.activeVideo) {
                    this.close();
                }
            });

            document.body.append(element);
            this.element = element;
            this.content = element.querySelector('[data-vl-video-modal-content]');
            this.closeButton = element.querySelector('.vl-video-modal__close');
        },

        open(owner, video, trigger) {
            if (!video) return;

            this.ensure();

            if (this.activeVideo) {
                this.close(false);
            }

            this.activeOwner = owner;
            this.activeTrigger = trigger;
            this.activeVideo = video;

            video.classList.add('vl-video-modal__video');
            video.autoplay = false;
            video.controls = true;
            video.muted = false;
            video.defaultMuted = false;
            video.playsInline = true;
            video.preload = 'auto';
            video.removeAttribute('autoplay');
            video.removeAttribute('muted');
            video.setAttribute('controls', 'controls');
            video.setAttribute('playsinline', 'playsinline');
            video.setAttribute('preload', 'auto');

            this.content.replaceChildren(video);
            this.element.classList.add('is-active');
            this.element.setAttribute('aria-hidden', 'false');
            document.documentElement.classList.add('vl-video-modal-open');

            video.load();
            video.play().catch(() => {
                // 浏览器阻止自动播放时，用户仍可通过原生控件开始播放。
            });
            this.closeButton.focus({ preventScroll: true });
        },

        close(restoreFocus = true) {
            if (!this.element) return;

            const trigger = this.activeTrigger;

            releaseVideo(this.activeVideo);
            this.content.replaceChildren();
            this.element.classList.remove('is-active');
            this.element.setAttribute('aria-hidden', 'true');
            document.documentElement.classList.remove('vl-video-modal-open');

            this.activeVideo = null;
            this.activeOwner = null;
            this.activeTrigger = null;

            if (restoreFocus && trigger?.isConnected) {
                trigger.focus({ preventScroll: true });
            }
        },

        closeForOwner(owner, restoreFocus = false) {
            if (this.activeOwner === owner) {
                this.close(restoreFocus);
            }
        }
    };

    if (!customElements.get('vl-video')) {
        class VLVideo extends HTMLElement {
            static PLAYBACK_TIMEOUT = 10000;

            constructor() {
                super();

                this.mediaQuery = window.matchMedia('(max-width: 767px)');
                this.intersectionObserver = null;
                this.activeVideo = null;
                this.activeVideoType = null;
                this.manualPlayVideoType = null;
                this.poster = null;
                this.playButtons = [];
                this.isInView = false;
                this.isInitialized = false;
                this.playbackTimeout = null;
                this.videoEventCleanup = null;
                this.statusControl = null;
                this.productCarousel = null;
                this.productCarouselSlide = null;
                this.waitingProductCarousel = null;

                this.handleIntersection = this.handleIntersection.bind(this);
                this.handleViewportChange = this.handleViewportChange.bind(this);
                this.handlePlayButtonClick = this.handlePlayButtonClick.bind(this);
                this.handleRetryClick = this.handleRetryClick.bind(this);
                this.handleProductCarouselSelect = this.handleProductCarouselSelect.bind(this);
                this.handleProductCarouselReady = this.handleProductCarouselReady.bind(this);
            }

            connectedCallback() {
                if (this.isInitialized) return;

                this.isInitialized = true;
                this.poster = this.querySelector('[data-vl-video-poster]');
                this.playButtons = Array.from(this.querySelectorAll('[data-vl-video-play]'));
                this.createStatusControl();
                this.setupProductCarousel();
                this.hasMobileVideo = Boolean(this.querySelector('template[data-vl-video-mobile]'));
                this.hasResponsiveBehavior = this.hasMobileVideo
                    || this.popPlayDesktop !== this.popPlayMobile
                    || this.autoplayDesktop !== this.autoplayMobile
                    || this.onlyDesktop
                    || this.onlyMobile;

                this.playButtons.forEach((button) => {
                    button.addEventListener('click', this.handlePlayButtonClick);
                });

                if (this.hasResponsiveBehavior) {
                    if (this.mediaQuery.addEventListener) {
                        this.mediaQuery.addEventListener('change', this.handleViewportChange);
                    } else {
                        this.mediaQuery.addListener(this.handleViewportChange);
                    }
                }

                if (this.loadWhenVisible && 'IntersectionObserver' in window) {
                    this.intersectionObserver = new IntersectionObserver(this.handleIntersection, {
                        root: null,
                        rootMargin: '0px',
                        threshold: 0.01
                    });
                    this.intersectionObserver.observe(this);
                    return;
                }

                this.isInView = true;
                this.activateVideo();
            }

            disconnectedCallback() {
                this.intersectionObserver?.disconnect();
                this.intersectionObserver = null;
                this.waitingProductCarousel?.removeEventListener('vl:carousel-ready', this.handleProductCarouselReady);
                this.waitingProductCarousel = null;
                this.productCarousel?.embla?.off('select', this.handleProductCarouselSelect);
                this.productCarousel = null;
                this.productCarouselSlide = null;

                if (this.hasResponsiveBehavior) {
                    if (this.mediaQuery.removeEventListener) {
                        this.mediaQuery.removeEventListener('change', this.handleViewportChange);
                    } else {
                        this.mediaQuery.removeListener(this.handleViewportChange);
                    }
                }

                this.playButtons.forEach((button) => {
                    button.removeEventListener('click', this.handlePlayButtonClick);
                });

                videoModal.closeForOwner(this);
                this.showPoster();
                this.manualPlayVideoType = null;
                this.isInView = false;
                this.isInitialized = false;
            }

            get loadWhenVisible() {
                return this.hasAttribute('load-when-visible');
            }

            get autoplayDesktop() {
                return this.hasAttribute('data-autoplay-desktop');
            }

            get autoplayMobile() {
                return this.hasAttribute('data-autoplay-mobile');
            }

            get popPlayDesktop() {
                return this.hasAttribute('data-pop-play-desktop');
            }

            get popPlayMobile() {
                return this.hasAttribute('data-pop-play-mobile');
            }

            get onlyDesktop() {
                return this.hasAttribute('data-only-desktop');
            }

            get onlyMobile() {
                return this.hasAttribute('data-only-mobile');
            }

            getCurrentVideoType() {
                return this.mediaQuery.matches ? 'mobile' : 'desktop';
            }

            getCurrentTemplate(videoType) {
                const template = this.querySelector(`template[data-vl-video-${videoType}]`);

                return template || this.querySelector('template[data-vl-video-desktop]');
            }

            shouldPopPlay(videoType) {
                return videoType === 'mobile' ? this.popPlayMobile : this.popPlayDesktop;
            }

            shouldAutoplay(videoType) {
                return videoType === 'mobile' ? this.autoplayMobile : this.autoplayDesktop;
            }

            isEnabledForVideoType(videoType) {
                if (this.onlyDesktop) return videoType === 'desktop';
                if (this.onlyMobile) return videoType === 'mobile';

                return true;
            }

            setupProductCarousel() {
                const carousel = this.closest('carousel-component[data-product-media-carousel-component]');
                const slide = this.closest('.carousel__slide[data-media-id]');

                if (!carousel || !slide) return;

                if (!carousel.embla) {
                    if (this.waitingProductCarousel !== carousel) {
                        this.waitingProductCarousel?.removeEventListener('vl:carousel-ready', this.handleProductCarouselReady);
                        carousel.addEventListener('vl:carousel-ready', this.handleProductCarouselReady, { once: true });
                        this.waitingProductCarousel = carousel;
                    }
                    return;
                }

                this.waitingProductCarousel?.removeEventListener('vl:carousel-ready', this.handleProductCarouselReady);
                this.waitingProductCarousel = null;
                this.productCarousel?.embla?.off('select', this.handleProductCarouselSelect);
                this.productCarousel = carousel;
                this.productCarouselSlide = slide;
                carousel.embla.on('select', this.handleProductCarouselSelect);
                this.handleProductCarouselSelect();
            }

            handleProductCarouselReady() {
                this.waitingProductCarousel = null;
                if (this.isConnected) this.setupProductCarousel();
            }

            isProductCarouselSlideSelected() {
                if (!this.productCarousel || !this.productCarouselSlide) return true;

                const { embla } = this.productCarousel;
                const selectedSlide = embla.slideNodes()[embla.selectedScrollSnap()];

                return selectedSlide === this.productCarouselSlide
                    || this.productCarouselSlide.classList.contains('is-selected');
            }

            handleProductCarouselSelect() {
                if (this.isProductCarouselSlideSelected()) {
                    if (!this.loadWhenVisible || this.isInView) this.activateVideo();
                    return;
                }

                this.showPoster();
            }

            handleIntersection(entries) {
                const entry = entries[0];

                if (!entry) return;

                this.isInView = entry.isIntersecting;

                if (this.isInView) {
                    this.activateVideo();
                    return;
                }

                if (this.activeVideoType && this.shouldAutoplay(this.activeVideoType)) {
                    this.activeVideo?.pause();
                }
            }

            handleViewportChange() {
                const videoType = this.getCurrentVideoType();

                videoModal.closeForOwner(this);
                this.manualPlayVideoType = null;

                if (!this.isEnabledForVideoType(videoType)) {
                    this.showPoster();
                    return;
                }

                if (this.loadWhenVisible && !this.isInView) {
                    if (this.shouldPopPlay(videoType) || !this.shouldAutoplay(videoType)) {
                        this.showPoster();
                    }
                    return;
                }

                this.activateVideo();
            }

            handlePlayButtonClick(event) {
                const videoType = this.getCurrentVideoType();

                if (!this.isEnabledForVideoType(videoType)) return;

                event.preventDefault();
                event.stopPropagation();

                if (!this.shouldPopPlay(videoType)) {
                    this.manualPlayVideoType = videoType;
                    this.activateVideo({ forcePlay: true });
                    return;
                }

                const template = this.getCurrentTemplate(videoType);
                const fragment = template?.content.cloneNode(true);
                const video = fragment?.querySelector('video');

                if (!video) return;

                videoModal.open(this, video, event.currentTarget);
            }

            createStatusControl() {
                if (!this.poster || this.statusControl) return;

                const control = document.createElement('button');
                control.className = 'vl-video__status-control';
                control.type = 'button';
                control.hidden = true;
                control.setAttribute('aria-live', 'polite');
                control.innerHTML = `
                    <span class="vl-video__spinner" aria-hidden="true"></span>
                    <svg class="vl-video__retry-icon" xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none">
                        <path d="M20.0004 6.77834C26.9641 6.77834 32.6804 12.2404 33.1079 19.1632C33.1363 19.6226 33.5067 19.9967 33.9669 19.9967H34.7036C35.1638 19.9967 35.5392 19.6231 35.5152 19.1635C35.0845 10.9233 28.303 4.375 20.0004 4.375C14.4095 4.375 9.50841 7.34428 6.77176 11.8V7.43963C6.77176 6.97939 6.39867 6.6063 5.93843 6.6063H5.20833C4.7481 6.6063 4.375 6.97939 4.375 7.43963V14.789C4.375 15.2492 4.7481 15.6223 5.20833 15.6223L12.5086 15.6223C12.9688 15.6223 13.3419 15.2492 13.3419 14.789V14.0458C13.3419 13.5855 12.9688 13.2124 12.5086 13.2124L8.72614 13.2124C11.0227 9.35526 15.2173 6.77834 20.0004 6.77834Z" fill="#F2F3F5"/>
                        <path d="M4.48557 20.8298C4.46154 20.3702 4.83694 19.9966 5.29718 19.9966H6.03385C6.49409 19.9966 6.86447 20.3707 6.89284 20.8301C7.32036 27.7529 13.0367 33.2151 20.0004 33.2151C24.7835 33.2151 28.9781 30.6381 31.2746 26.781H27.4914C27.0312 26.781 26.6581 26.4079 26.6581 25.9476V25.2044C26.6581 24.7442 27.0312 24.3711 27.4914 24.3711H34.7917C35.2519 24.3711 35.625 24.7442 35.625 25.2044V32.5538C35.625 33.014 35.2519 33.3871 34.7917 33.3871H34.0616C33.6013 33.3871 33.2282 33.014 33.2282 32.5538V28.1946C30.4914 32.6497 25.5907 35.6184 20.0004 35.6184C11.6978 35.6184 4.91624 29.07 4.48557 20.8298Z" fill="#F2F3F5"/>
                    </svg>
                    <span class="visually-hidden">Retry video</span>
                `;
                control.addEventListener('click', this.handleRetryClick);
                this.poster.append(control);
                this.statusControl = control;
            }

            setLoadingState() {
                this.createStatusControl();
                this.classList.add('is-loading');
                this.classList.remove('is-playing', 'is-error');

                if (this.statusControl) {
                    this.statusControl.hidden = false;
                    this.statusControl.disabled = true;
                    this.statusControl.setAttribute('aria-label', 'Video is loading');
                }
            }

            setPlayingState() {
                this.clearPlaybackTimeout();
                this.classList.remove('is-loading', 'is-error');
                this.classList.add('is-playing');

                if (this.statusControl) this.statusControl.hidden = true;
            }

            setRetryState() {
                this.clearPlaybackTimeout();
                this.classList.remove('is-loading', 'is-playing');
                this.classList.add('is-error');

                if (this.statusControl) {
                    this.statusControl.hidden = false;
                    this.statusControl.disabled = false;
                    this.statusControl.setAttribute('aria-label', 'Retry video');
                }
            }

            clearVideoState() {
                this.clearPlaybackTimeout();
                this.classList.remove('is-loading', 'is-playing', 'is-error');

                if (this.statusControl) {
                    this.statusControl.hidden = true;
                    this.statusControl.disabled = false;
                }
            }

            startPlaybackTimeout(video) {
                this.clearPlaybackTimeout();
                this.playbackTimeout = window.setTimeout(() => {
                    if (this.activeVideo === video && video.paused) this.setRetryState();
                }, VLVideo.PLAYBACK_TIMEOUT);
            }

            clearPlaybackTimeout() {
                if (this.playbackTimeout !== null) {
                    window.clearTimeout(this.playbackTimeout);
                    this.playbackTimeout = null;
                }
            }

            bindVideoEvents(video) {
                this.unbindVideoEvents();

                const handlePlaying = () => {
                    if (this.activeVideo === video) this.setPlayingState();
                };
                const handleError = () => {
                    if (this.activeVideo === video) this.setRetryState();
                };

                video.addEventListener('playing', handlePlaying);
                video.addEventListener('error', handleError);
                this.videoEventCleanup = () => {
                    video.removeEventListener('playing', handlePlaying);
                    video.removeEventListener('error', handleError);
                };
            }

            unbindVideoEvents() {
                this.videoEventCleanup?.();
                this.videoEventCleanup = null;
            }

            handleRetryClick(event) {
                event.preventDefault();
                event.stopPropagation();

                const videoType = this.getCurrentVideoType();
                if (!this.activeVideo || this.activeVideoType !== videoType) {
                    this.manualPlayVideoType = videoType;
                    this.activateVideo({ forcePlay: true });
                    return;
                }

                this.activeVideo.load();
                this.playVideo(this.activeVideo);
            }

            activateVideo({ forcePlay = false } = {}) {
                const videoType = this.getCurrentVideoType();

                if (!this.isProductCarouselSlideSelected()) {
                    this.showPoster();
                    return;
                }

                if (!this.isEnabledForVideoType(videoType)) {
                    this.showPoster();
                    return;
                }

                if (this.shouldPopPlay(videoType)) {
                    this.showPoster();
                    return;
                }

                const shouldAutoplay = this.shouldAutoplay(videoType);
                const hasManualPlayRequest = this.manualPlayVideoType === videoType;

                if (!shouldAutoplay && !hasManualPlayRequest) {
                    this.showPoster();
                    return;
                }

                if (this.activeVideo && this.activeVideoType === videoType) {
                    if (forcePlay || (shouldAutoplay && (!this.loadWhenVisible || this.isInView))) {
                        this.playVideo(this.activeVideo);
                    }
                    return;
                }

                const template = this.getCurrentTemplate(videoType);

                if (!template) return;

                const fragment = template.content.cloneNode(true);
                const video = fragment.querySelector('video');

                if (!video) return;

                video.autoplay = false;

                const previousVideo = this.activeVideo;

                if (previousVideo) {
                    this.destroyVideo(previousVideo);
                    previousVideo.remove();
                }

                video.classList.add('vl-video__inline-video');

                if (this.poster?.isConnected) {
                    this.poster.insertAdjacentElement('afterend', video);
                } else {
                    this.append(video);
                }

                this.activeVideo = video;
                this.activeVideoType = videoType;

                this.bindVideoEvents(video);
                this.setLoadingState();

                video.load();

                if (forcePlay || (shouldAutoplay && (!this.loadWhenVisible || this.isInView))) {
                    this.playVideo(video);
                }
            }

            showPoster() {
                const video = this.activeVideo;

                this.activeVideo = null;
                this.activeVideoType = null;
                this.clearVideoState();

                if (video) {
                    this.destroyVideo(video);
                    video.remove();
                }
            }

            destroyVideo(video) {
                this.unbindVideoEvents();
                releaseVideo(video);
            }

            playVideo(video) {
                if (!video) return;

                this.setLoadingState();
                this.startPlaybackTimeout(video);
                video.play().catch(() => {
                    if (this.activeVideo === video) this.setRetryState();
                    // 浏览器拦截自动播放时，保留视频封面或当前画面。
                });
            }
        }

        customElements.define('vl-video', VLVideo);
    }
})();
